/**
 * @file column-shares.ts
 * @description Доли колонок из 12 (phase19.16, партия SB120, этап 1
 * «секция-герой»; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-stage1.md §2.5, вопрос 2 —
 * «двенадцатые с перетаскиванием границы и −/+»; данные
 * db-design-sb120-stage1.md §1 `columns.shares`).
 *
 * Чистые функции: сумма всегда 12, доля не меньше 1. Граница между
 * колонками i и i+1 двигается по двенадцатым; «+» у колонки берёт долю у
 * соседа (правого, если у него есть лишняя, иначе левого), «−» отдаёт её
 * соседу. Быстрые схемы — прежние пропорции v2 (`SECTION_RATIO_SHARES`).
 */

import { SECTION_LOOK_LIMITS, SECTION_RATIO_SHARES, SECTION_RATIOS, type SectionRatio } from "@chaiberi/shared";

const { min: MIN, total: TOTAL } = SECTION_LOOK_LIMITS.shares;

/** Сумма долей до колонки `i` (не включая). */
export function sharesBefore(shares: readonly number[], i: number): number {
  return shares.slice(0, i).reduce((sum, v) => sum + v, 0);
}

/**
 * Поставить границу после колонки `i` на позицию `at` (двенадцатые от
 * левого края). Позиция ограничивается так, чтобы обеим колонкам осталось
 * не меньше 1.
 */
export function placeBoundary(shares: readonly number[], i: number, at: number): number[] {
  const left = shares[i];
  const right = shares[i + 1];
  if (left === undefined || right === undefined) return [...shares];
  const start = sharesBefore(shares, i);
  const pos = Math.min(start + left + right - MIN, Math.max(start + MIN, Math.round(at)));
  const next = [...shares];
  next[i] = pos - start;
  next[i + 1] = left + right - (pos - start);
  return next;
}

/** Сдвинуть границу после колонки `i` на `delta` двенадцатых. */
export function moveBoundary(shares: readonly number[], i: number, delta: number): number[] {
  return placeBoundary(shares, i, sharesBefore(shares, i + 1) + delta);
}

/** «+» / «−» у колонки: сосед справа, иначе слева; сумма остаётся 12. */
export function bumpShare(shares: readonly number[], i: number, dir: 1 | -1): number[] {
  const own = shares[i];
  if (own === undefined) return [...shares];
  const neighbours = [i + 1, i - 1].filter((j) => shares[j] !== undefined);
  const donor = dir === 1 ? neighbours.find((j) => (shares[j] ?? 0) > MIN) : neighbours[0];
  if (donor === undefined || (dir === -1 && own <= MIN)) return [...shares];
  const next = [...shares];
  next[i] = own + dir;
  next[donor] = (next[donor] ?? 0) - dir;
  return next;
}

/** Можно ли «+» / «−» у колонки. */
export function canBump(shares: readonly number[], i: number, dir: 1 | -1): boolean {
  return bumpShare(shares, i, dir).some((v, j) => v !== shares[j]);
}

/** «7/12 + 5/12»; одна колонка — «во всю». */
export function sharesLabel(shares: readonly number[]): string {
  return shares.length <= 1 ? "во всю" : shares.map((v) => `${v}/${TOTAL}`).join(" + ");
}

/** Быстрые схемы для числа колонок (прежние пропорции v2). */
export function quickSchemes(count: number): Array<{ ratio: SectionRatio; shares: readonly number[] }> {
  return SECTION_RATIOS.map((ratio) => ({ ratio, shares: SECTION_RATIO_SHARES[ratio] })).filter((s) => s.shares.length === count);
}

/** Пропорция v2, совпадающая с долями; нет — `null`. */
export function ratioOfShares(shares: readonly number[]): SectionRatio | null {
  return SECTION_RATIOS.find((r) => sameShares(SECTION_RATIO_SHARES[r], shares)) ?? null;
}

export function sameShares(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Доля в процентах для подсказки: 7/12 → «58 %». */
export function sharePercent(share: number): string {
  return `${Math.round((share / TOTAL) * 100)} %`;
}
